import { signal } from "@angular/core";
import { debounce, SchemaPathTree, validate } from "@angular/forms/signals";

const MIN_SELECTABLE_DATE = new Date(1990, 0, 31);

export interface TimePeriodFilterModel {
    fromDate: Date | null;
    toDate: Date | null;
    expanded: boolean;
    useFromToDate: boolean;
    numberOfMonthsToShow: number;
}

export function createTimePeriodModel() {
    return signal<TimePeriodFilterModel>({
        fromDate: null,
        toDate: null,
        expanded: true,
        useFromToDate: false,
        numberOfMonthsToShow: 12,
    });
}

export function buildTimePeriodModel(a: SchemaPathTree<TimePeriodFilterModel>) {
    debounce(a.fromDate, 300);
    debounce(a.toDate, 300);
    withinSelectableRange(a.fromDate, "Fra");
    withinSelectableRange(a.toDate, "Til");
    fromDateNotAfterToDate(a.fromDate, a.toDate);
}

function endOfToday(): Date {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
}

function formatDate(date: Date): string {
    return date.toLocaleDateString("nb-NO", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function withinSelectableRange(field: SchemaPathTree<Date | null>, label: "Fra" | "Til") {
    validate(field, ({ value }) => {
        const date = value();
        if (date == null) return null;

        if (+date < +MIN_SELECTABLE_DATE) {
            return {
                kind: "belowLimit",
                message: `${label}-dato kan ikke være tidligere enn ${formatDate(MIN_SELECTABLE_DATE)}.`,
            };
        }
        if (+date > +endOfToday()) {
            return {
                kind: "aboveLimit",
                message: `${label}-dato kan ikke være fram i tid.`,
            };
        }
        return null;
    });
}

function fromDateNotAfterToDate(fromDateField: SchemaPathTree<Date | null>, toDateField: SchemaPathTree<Date | null>) {
    validate(fromDateField, ({ value, valueOf }) => {
        const fromDate = value();
        const toDate = valueOf(toDateField);
        if (fromDate == null || toDate == null) return null;

        return +fromDate > +toDate
            ? {
                  kind: "aboveMax",
                  message: "Fra-verdi er høyere enn Til-verdi.",
              }
            : null;
    });
}
